// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene - AI/Tech theme with blue/purple accents
export const SCENE_COLORS = {
  scene1: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Tech, ChatGPT
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Positive (5 businesses)
  scene3: { primaryColor: '#EF4444', accentColor: '#FCD34D' },   // Red/Gold - Alarming (97%)
  scene4: { primaryColor: '#FFFFFF', accentColor: '#EF4444' },   // White/Red - Muted, concerning
  scene5: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Solution
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA Premium
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/pvm-invisible-test/scene1-chatgpt.png',
  scene2: 'images/pvm-invisible-test/scene2-results.png',
  scene3: 'images/pvm-invisible-test/scene3-invisible.png',
  scene4: 'images/pvm-quiet-phone/scene4-paving-job.png',
  scene5: 'images/pvm-invisible-test/scene5-analytics.png',
  scene6: 'images/pvm-invisible-test/scene6-report.png',
};

// Hg market report presentation slides (reuse from pvm quiet phone)
export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

// Dramatic words for "The Invisible Test" script
export const DRAMATIC_WORDS = ['97%', 'invisible.', 'Completely', 'Free', 'Report.'];

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
// Voice: Mark (UgBBYS2sOqTuMpoF3BR0) - American male
// Manual corrections: merged "97" + "%" into "97%", normalized punctuation/casing
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Hook - ChatGPT question
  { text: 'I', startMs: 0, endMs: 80 },
  { text: 'asked', startMs: 80, endMs: 360 },
  { text: 'ChatGPT:', startMs: 360, endMs: 1420 },
  { text: 'best', startMs: 1420, endMs: 2200 },
  { text: 'paving', startMs: 2200, endMs: 2600 },
  { text: 'company', startMs: 2600, endMs: 2820 },
  { text: 'near', startMs: 2820, endMs: 3200 },
  { text: 'me.', startMs: 3200, endMs: 3600 },

  // Scene 2: Result
  { text: 'It', startMs: 3600, endMs: 4120 },
  { text: 'recommended', startMs: 4120, endMs: 4480 },
  { text: '5', startMs: 4480, endMs: 4780 },
  { text: 'businesses.', startMs: 4780, endMs: 5820 },

  // Scene 3: Stat - 97% invisible (merged "97" + "%")
  { text: '97%', startMs: 5820, endMs: 7420 },
  { text: 'of', startMs: 7420, endMs: 7680 },
  { text: 'paving', startMs: 7680, endMs: 7940 },
  { text: 'contractors?', startMs: 7940, endMs: 8740 },
  { text: 'Completely', startMs: 8740, endMs: 9460 },
  { text: 'invisible.', startMs: 9460, endMs: 10220 },

  // Scene 4: Pain points
  { text: 'Not', startMs: 10220, endMs: 10880 },
  { text: 'recommended.', startMs: 10880, endMs: 11540 },
  { text: 'Not', startMs: 11540, endMs: 11780 },
  { text: 'mentioned.', startMs: 11780, endMs: 12320 },
  { text: 'Not', startMs: 12320, endMs: 12580 },
  { text: 'getting', startMs: 12580, endMs: 12820 },
  { text: 'those', startMs: 12820, endMs: 13000 },
  { text: 'calls.', startMs: 13000, endMs: 13700 },

  // Scene 5: Solution
  { text: "We'll", startMs: 13700, endMs: 14140 },
  { text: 'show', startMs: 14140, endMs: 14300 },
  { text: 'you', startMs: 14300, endMs: 14420 },
  { text: 'exactly', startMs: 14420, endMs: 14700 },
  { text: 'where', startMs: 14700, endMs: 14980 },
  { text: 'you', startMs: 14980, endMs: 15080 },
  { text: 'stand.', startMs: 15080, endMs: 15480 },
  { text: 'Your', startMs: 15480, endMs: 15880 },
  { text: 'AI', startMs: 15880, endMs: 16120 },
  { text: 'visibility.', startMs: 16120, endMs: 16860 },
  { text: 'Your', startMs: 16860, endMs: 17360 },
  { text: 'competitors.', startMs: 17360, endMs: 18060 },
  { text: 'The', startMs: 18060, endMs: 18400 },
  { text: 'gaps.', startMs: 18400, endMs: 18980 },

  // Scene 6: CTA
  { text: 'Free', startMs: 18980, endMs: 19660 },
  { text: 'Market', startMs: 19660, endMs: 19900 },
  { text: 'Report.', startMs: 19900, endMs: 20640 },
  { text: 'Ten', startMs: 20640, endMs: 21660 },
  { text: 'minutes.', startMs: 21660, endMs: 22020 },
  { text: 'Link', startMs: 22020, endMs: 22260 },
  { text: 'below.', startMs: 22260, endMs: 22720 },
];

// Scene definitions with word groupings (aligned to Whisper timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 3600,
    words: WORD_TIMINGS.slice(0, 8),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Result',
    startMs: 3600,
    endMs: 5820,
    words: WORD_TIMINGS.slice(8, 12),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Stat',
    startMs: 5820,
    endMs: 10220,
    words: WORD_TIMINGS.slice(12, 18),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 10220,
    endMs: 13700,
    words: WORD_TIMINGS.slice(18, 26),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Solution',
    startMs: 13700,
    endMs: 18980,
    words: WORD_TIMINGS.slice(26, 40),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 18980,
    endMs: 22720,
    words: WORD_TIMINGS.slice(40),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - full animated logo playback
export const LOGO_SCENE = {
  startMs: 22720,
  endMs: 27820, // ~5s logo duration
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
